export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Splits `text` into segments, marking every case-insensitive occurrence of
 * each whitespace-separated word of `query`. Overlapping or adjacent matches
 * merge into one segment. An empty query yields the text as one plain
 * segment.
 */
export function highlightMatches(
  text: string,
  query: string,
): HighlightSegment[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!text || !words.length) return [{ text, match: false }];

  const lower = text.toLowerCase();
  const ranges: [number, number][] = [];
  for (const word of words) {
    let from = 0;
    for (;;) {
      const at = lower.indexOf(word, from);
      if (at < 0) break;
      ranges.push([at, at + word.length]);
      from = at + 1;
    }
  }
  if (!ranges.length) return [{ text, match: false }];

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }

  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), match: false });
    }
    segments.push({ text: text.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false });
  }
  return segments;
}
